import React, { useState, useEffect, useRef } from "react";
import { Box } from "@mui/material";

import { ContainerBg } from "../../../../useGeneralStyles";

import Bar from "./Components/Bar";
import ListQuantityOfProducts from "../ListQuantityOfProducts/ListQuantityOfProducts";
import { IReport, IGetReport, IGetReportResponse } from "../../../../api/billing/index.types";
import { ContainerWithLoader, SelectByDateWithCustomPeriod } from "../../../../components/ui";
import { IntlFormatUSD } from "../../../../utils";
import { CENTS_IN_DOLLAR } from "../../../../constants";

interface IProps {
  purchaseStatus: string;
  type: "sum" | "purchased" | "refunded" | "chargeback";
  isShowList?: boolean;
  getReportRequest: (body: IGetReport) => Promise<IGetReportResponse>;
  broker?: string;
}

const ReportChartWithDateSelect = ({
  purchaseStatus,
  type,
  isShowList,
  getReportRequest,
  broker,
}: IProps): React.ReactElement => {
  const [isLoading, setIsLoading] = useState(false);
  const [report, setReport] = useState<IReport[]>([]);
  const lastDatesRef = useRef<{ startDate: string; endDate: string; isAllTime?: boolean } | null>(
    null
  );

  const fetchReport = async (startDate: string, endDate: string, isAllTime?: boolean) => {
    setIsLoading(true);
    try {
      const response = await getReportRequest({
        startDate: `${startDate}`,
        endDate: `${endDate}`,
        purchaseStatus,
        isAllTime,
        broker,
      });
      const reportResponse = response.data.report || [];

      setReport(reportResponse);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  };

  const getReport = (startDate: string, endDate: string, isAllTime?: boolean) => {
    lastDatesRef.current = { startDate, endDate, isAllTime };
    fetchReport(startDate, endDate, isAllTime);
  };

  useEffect(() => {
    if (lastDatesRef.current && broker !== undefined) {
      const { startDate, endDate, isAllTime } = lastDatesRef.current;
      fetchReport(startDate, endDate, isAllTime);
    }
  }, [broker]);

  const isNegativeValue = type === "chargeback" || type === "refunded";

  const sum = report
    .map(({ sum_price_usd, sum_price_usd_resets }) => +sum_price_usd + Number(sum_price_usd_resets))
    .reduce((a, b) => a + b, 0);

  const isTypePurchased = type === "purchased";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
      <Box sx={{ display: "flex", flexWrap: "wrap" }}>
        <SelectByDateWithCustomPeriod
          setIsLoading={setIsLoading}
          getReport={getReport}
          setReport={setReport}
        />

        <h3 style={{ margin: "0 0 0 10px" }}>
          {!isLoading && !isTypePurchased && (
            <>
              Sum:{" "}
              <span>
                {isNegativeValue && "-"}
                {IntlFormatUSD(sum / CENTS_IN_DOLLAR)}
              </span>
            </>
          )}
        </h3>
      </Box>

      <ContainerBg
        sx={{
          margin: "initial",
          marginTop: "10px",
        }}
      >
        <ContainerWithLoader isLoading={isLoading} report={report}>
          {isShowList ? (
            <ListQuantityOfProducts report={report} />
          ) : (
            <Bar type={type} report={report} />
          )}
        </ContainerWithLoader>
      </ContainerBg>
    </Box>
  );
};

export default ReportChartWithDateSelect;
